import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  canonical,
  type = 'website',
  image = 'https://solarch.in/hero-bg.webp',
  noindex = false,
  jsonLd,
}: SEOProps) {
  // Normalize canonical URL to always use https://solarch.in
  const fullCanonical = canonical
    ? canonical === '/'
      ? 'https://solarch.in/'
      : `https://solarch.in${canonical.startsWith('/') ? canonical : `/${canonical}`}`.replace(/\/+$/, '')
    : undefined;

  const robotsDirective = noindex ? 'noindex, follow' : 'index, follow';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="robots" content={robotsDirective} />
      
      {/* Canonical Link */}
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      {fullCanonical && <meta property="twitter:url" content={fullCanonical} />}
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Structured Data (JSON-LD) */}
      {jsonLd &&
        (Array.isArray(jsonLd) ? (
          jsonLd.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        ))}
    </Helmet>
  );
}
